import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInLeft, scaleIn, staggerContainer } from '../../utils/animations.ts';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { getLogoPath } from '../../utils/themeHelpers.ts';
import { useAdminAuth } from '../../contexts/AdminAuthContext.tsx';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, onCollapseChange }) => {
  const { currentTheme } = useTheme();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { logout } = useAdminAuth();
  const navigate = useNavigate();

  const handleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    if (onCollapseChange) {
      onCollapseChange(newCollapsed);
    }
  };

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/admin',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
        </svg>
      )
    },
    {
      name: 'Blogs',
      path: '/admin/blogs',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      )
    },
    {
      name: 'Bookings',
      path: '/admin/bookings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      name: 'Pricing Packages',
      path: '/admin/pricing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    {
      name: 'Subscribers',
      path: '/admin/subscribers',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14a7 7 0 00-7 7m14 0a7 7 0 00-7-7m7 7h3m-3-4h3m-3-4h3" />
        </svg>
      )
    },
    {
      name: 'Testimonials',
      path: '/admin/testimonials',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h8m-8 4h6M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      name: 'Theme Settings',
      path: '/admin/theme-settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      )
    },
 
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <motion.div
        className={`fixed top-0 left-0 h-full bg-white shadow-lg z-50 transform transition-all duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isCollapsed ? 'w-16' : 'w-64'}`}
        initial={{ x: -256 }}
        animate={{ x: isOpen ? 0 : -256 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header with Collapse Button */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          {!isCollapsed && (
            <motion.div
              variants={scaleIn}
              initial="initial"
              animate="animate"
              className="flex-1"
            >
              <Link to="/admin" className="flex items-center space-x-2">
                <img 
                  src={getLogoPath(currentTheme)} 
                  alt="Life Coach Logo" 
                  className="w-auto object-contain"
                  style={{ height: '40px' }}
                />
                <span className="text-lg font-bold text-gray-900">Admin</span>
              </Link>
            </motion.div>
          )}
          <div className="flex items-center space-x-2">
            {/* Collapse/Expand Button */}
            <button
              onClick={handleCollapse}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
            {/* Mobile Close Button */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>

          {/* Menu Items */}
          <motion.nav 
            className="space-y-2"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {menuItems.map((item, index) => (
              <motion.div
                key={item.name}
                variants={fadeInLeft}
                custom={index}
              >
                <Link
                  to={item.path}
                  className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-all duration-200 group ${
                    location.pathname === item.path
                      ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    // Only close on mobile screens
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  title={isCollapsed ? item.name : ''}
                >
                  <motion.div
                    className="flex-shrink-0"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.icon}
                  </motion.div>
                  {!isCollapsed && (
                    <span className="font-medium group-hover:translate-x-1 transition-transform duration-200">
                      {item.name}
                    </span>
                  )}
                </Link>
              </motion.div>
            ))}
          </motion.nav>

          {/* User Info */}
          {!isCollapsed && (
            <motion.div 
              className="absolute bottom-6 left-4 right-4"
              variants={fadeInLeft}
              custom={menuItems.length}
            >
              <div className="bg-gray-50 rounded-lg p-4 flex flex-col items-center space-y-3">
                <div className="flex items-center space-x-3">
                  <motion.div 
                    className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="text-white font-semibold text-sm">A</span>
                  </motion.div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Admin User</p>
                    <p className="text-gray-500 text-xs">admin@lifecoach.com</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    logout();
                    navigate('/admin-login', { replace: true });
                  }}
                  className="w-full text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-2 rounded-md text-center transition-colors"
                >
                  Logout
                </button>
              </div>
            </motion.div>
          )}
          {isCollapsed && (
            <motion.div 
              className="absolute bottom-6 left-0 right-0 flex justify-center"
              variants={fadeInLeft}
              custom={menuItems.length}
            >
              <motion.div 
                className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
                title="Admin User"
              >
                <span className="text-white font-semibold text-sm">A</span>
              </motion.div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
